/**
 * Meta Pixel Tracking Composable
 *
 * Provides client-side event tracking for cart and checkout actions using Facebook Pixel.
 */

// Get tracking settings from the page (injected by backend)
const metaPixelSettings = window._metaPixel || {};

/**
 * Check if a specific tracking event is enabled
 */
function isTrackingEnabled(eventType) {
    return metaPixelSettings[eventType] === true || metaPixelSettings[eventType] === '1' || metaPixelSettings[eventType] === 1;
}

/**
 * Check if Meta Pixel (fbq) is loaded
 */
function isMetaPixelLoaded() {
    return typeof window.fbq !== 'undefined';
}

/**
 * Track Meta Pixel event
 */
function trackMetaEvent(eventName, eventData) {
    if (!isMetaPixelLoaded()) {
        console.warn('⚠️ Meta Pixel (fbq) is not loaded');
        return;
    }

    window.fbq('track', eventName, eventData);
}

/**
 * Format product data for Meta Pixel
 * Adapts cart item data to Meta Pixel's expected format
 */
function formatProductForMeta(product, quantity = null) {
    // Extract IDs from cart item
    const contentIds = [String(product.product_id || product.id)];

    // Add variant ID if available
    if (product.variant_id) {
        contentIds.push(String(product.variant_id));
    }

    // Build content name
    let contentName = product.product_name || product.name || '';
    if (product.variant_name || product.variant) {
        contentName += ' - ' + (product.variant_name || product.variant);
    }

    return {
        content_type: 'product',
        content_ids: contentIds,
        content_name: contentName,
        value: parseFloat(product.price || 0) * (quantity || product.quantity || 1),
        currency: 'AUD',
    };
}

/**
 * Track add to cart event
 */
export function trackMetaAddToCart(product, quantity = 1) {
    console.log('🔍 trackMetaAddToCart called', {
        product,
        quantity,
        metaPixelSettings,
        isEnabled: isTrackingEnabled('add_to_cart')
    });

    if (!isTrackingEnabled('add_to_cart')) {
        console.warn('⚠️ Meta Pixel add_to_cart tracking is disabled');
        return;
    }

    if (!isMetaPixelLoaded()) {
        console.error('❌ Meta Pixel (fbq) is not loaded');
        return;
    }

    const eventData = formatProductForMeta(product, quantity);

    trackMetaEvent('AddToCart', eventData);

    console.group('🏷️ Meta Pixel Event: AddToCart');
    console.log('Event Data:', eventData);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
}

/**
 * Track remove from cart event
 */
export function trackMetaRemoveFromCart(cartItem) {
    if (!isTrackingEnabled('remove_from_cart')) return;
    if (!isMetaPixelLoaded()) return;

    const eventData = formatProductForMeta(cartItem);

    trackMetaEvent('RemoveFromCart', eventData);

    console.group('🏷️ Meta Pixel Event: RemoveFromCart');
    console.log('Event Data:', eventData);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
}

/**
 * Composable export
 */
export function useMetaTracking() {
    return {
        trackMetaAddToCart,
        trackMetaRemoveFromCart,
        isTrackingEnabled,
    };
}
